/* eslint-disable jsx-a11y/label-has-associated-control */
import ErrorIcon from '@mui/icons-material/Error';
import { Checkbox, FormControl, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { IFilter } from 'props';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  IClientViewModel,
  ICorrespondenceViewModel,
  ICorrespondentResultViewModel,
  IDistrictViewModel,
  ISpecialtyViewModel,
} from 'viewModels';
import { IDistrictInputModel } from 'inputModels';
import { Info } from '@mui/icons-material';
import { ReactComponent as IconClean } from '../../assets/icons/Icon-to-clean.svg';
import { ReactComponent as Filter } from '../../assets/icons/iconFilter.svg';
import { useApp, useAuth } from '../../context';
import { useClients } from '../../context/clients';
import { useCorrespondences } from '../../context/correspondences';
import { useSpecialties } from '../../context/specialties';
import {
  AudienceType,
  CorrespondenceType,
  GetSolicitationFilterDateType,
  labelAudienceType,
  labelCorrespondenceType,
  labelPaymentStatus,
  labelServiceStatus,
  PaymentSolicitationStatus,
  ServiceStatus,
  TypeUser,
} from '../../types/enums';
import { Container, PanelFilter } from './styles';
import { useSolicitation } from '../../context/solicitation';
import { Input } from '../Inputs/Input';
import { BRAZILIAN_STATES, MASK_PROCESS_NUMBER, UF_CODE_BY_NAME } from '../../constants';
import { DropDownList } from '../DropDownList';
import { isValidDate } from '../../utils/helpers';
import { unMask } from '../../utils/removeMask';
import { usePayment } from '../../context/payment';
import { DistrictController } from '../../controller/DistrictController';

interface IBaseFilterLeftProps {
  onChangeFilter: (data: IFilter) => void;
  displayOptions: {
    client: boolean;
    status: boolean;
    correspondenceType: boolean;
    date: boolean;
    especilty: boolean;
    correspondence: boolean;
    correspondent: boolean;
    court: boolean;
    audienceType: boolean;
    folderName: boolean;
    partName: boolean;
    protocol: boolean;
    processNumber: boolean;
    paymentStatus: boolean;
    district: boolean;
  };
  toFinancial?: boolean;
}

type TypeFilterItem =
  | 'correspondence'
  | 'specialty'
  | 'client'
  | 'status'
  | 'department'
  | 'correspondent'
  | 'court'
  | 'audienceType'
  | 'folderNumber'
  | 'partName'
  | 'protocol'
  | 'processNumber'
  | 'typeDemand'
  | 'paymentStatus'
  | 'dostrictUfAndName';

export function BaseFilterLeft({
  onChangeFilter,
  displayOptions,
  toFinancial,
}: IBaseFilterLeftProps) {
  const { user } = useAuth();
  const { correspondences } = useCorrespondences();
  const { specialties } = useSpecialties();
  const { clients, setEnableClientsLoad } = useClients();
  const { setFilter, filter } = useSolicitation();
  const {
    paymentCorrespondents,
    hasNextPageCorrespondents,
    isFetchingNextPageCorrespondents,
    fetchNextPageCorrespondents,
  } = usePayment();
  const { setLoading } = useApp();

  const [districts, setDistricts] = useState<IDistrictViewModel[]>([]);
  const [solicitationDateType, setSolicitationDateType] = useState<GetSolicitationFilterDateType>(
    GetSolicitationFilterDateType.FROM_START,
  );
  const [specialitiesSelected, setSpecialitiesSelected] = useState<ISpecialtyViewModel[]>([]);
  const [correspondencesSelected, setCorrespondenceSelected] = useState<ICorrespondenceViewModel[]>(
    [],
  );
  const [clientsSelected, setClientsSelected] = useState<IClientViewModel[]>([]);
  const [courtsSelected, setCourtsSelected] = useState<string[]>([]);
  const [statusSelected, setStatusSelected] = useState<string[]>([]);
  const [paymentStatusSelected, setPaymentStatusSelected] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [hasErrorDate, setHasErrorDate] = useState({ hasError: false, message: '' });
  const [demandTypeSelected, setDemandTypeSelected] = useState<string[]>([]);
  const [typeAudienceSelected, setTypeAudienceSelected] = useState<string[]>([]);
  const [correspondentsSelected, setCorrespondentsSelected] = useState<
    ICorrespondentResultViewModel[]
  >([]);
  const [foldersNumersSelected, setFoldersNumersSelected] = useState<string[]>([]);
  const [partsNamesSelected, setPartsNamesSelected] = useState<string[]>([]);
  const [processNumbersSelected, setProcessNumbersSelected] = useState<string[]>([]);
  const [protocolsSelected, setProtocolsSelected] = useState<string[]>([]);
  const [districtsSelected, setDistrictsSelected] = useState<IDistrictInputModel[]>([]);
  const [baseCorrespondences, setBaseCorrespondences] =
    useState<ICorrespondenceViewModel[]>(correspondences);

  const defaultValues = {
    protocol: '',
    processNumber: '',
    folderNumber: '',
    partName: '',
    court: '',
    districtUfCode: 0,
  };

  const { setValue, control } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const [protocolNumber, processNumber, folderNumber, partName, court, districtUfCode] = useWatch({
    control,
    name: ['protocol', 'processNumber', 'folderNumber', 'partName', 'court', 'districtUfCode'],
  });

  const handleCleanFilter = () => {
    setValue('court', '');
    setValue('folderNumber', '');
    setValue('partName', '');
    setValue('protocol', '');
    setValue('processNumber', '');
    setStartDate(null);
    setEndDate(null);
    setSpecialitiesSelected([]);
    setCorrespondenceSelected([]);
    setClientsSelected([]);
    setCourtsSelected([]);
    setStatusSelected([]);
    setProcessNumbersSelected([]);
    setFoldersNumersSelected([]);
    setProtocolsSelected([]);
    setPartsNamesSelected([]);
    setDemandTypeSelected([]);
    setTypeAudienceSelected([]);
    setSolicitationDateType(GetSolicitationFilterDateType.FROM_START);
    setCorrespondentsSelected([]);
    onChangeFilter?.({});
  };

  const handleAddItemFilterSelected = (newItem: string, type: TypeFilterItem) => {
    if (type === 'dostrictUfAndName' && newItem !== '') {
      if (!newItem || districtsSelected.some((d) => d.id === newItem)) return;
      const district = districts?.find((d) => d.id === newItem);
      if (!district) return;
      setDistrictsSelected((prevDistricts) => {
        const updatedDistricts = [
          ...prevDistricts,
          {
            id: district?.id,
            name: district?.name,
            ufCode: district?.ufCode,
          } as IDistrictInputModel,
        ];

        const newFilter = {
          ...filter,
          districts: updatedDistricts,
        };

        setFilter(newFilter);
        onChangeFilter(newFilter);

        return updatedDistricts;
      });
    }

    if (type === 'protocol') {
      if (!newItem || protocolsSelected.includes(newItem)) return;

      setProtocolsSelected([...protocolsSelected, newItem.trim()]);
      const newFilter = { ...filter, protocols: [...protocolsSelected, newItem.trim()] };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('protocol', '');
    }

    if (type === 'processNumber') {
      if (!newItem || processNumbersSelected.includes(newItem.trim())) return;

      setProcessNumbersSelected([...processNumbersSelected, newItem]);
      const newFilter = {
        ...filter,
        processNumbers: [...processNumbersSelected, unMask(newItem.trim())],
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('processNumber', '');
    }

    if (type === 'folderNumber') {
      if (!newItem || foldersNumersSelected.includes(newItem.trim())) return;

      setFoldersNumersSelected([...foldersNumersSelected, newItem]);
      const newFilter = { ...filter, foldersNumbers: [...foldersNumersSelected, newItem.trim()] };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('folderNumber', '');
    }

    if (type === 'partName') {
      if (!newItem || partsNamesSelected.includes(newItem)) return;

      setPartsNamesSelected([...partsNamesSelected, newItem]);
      const newFilter = { ...filter, partsNames: [...partsNamesSelected, newItem] };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('partName', '');
    }

    if (type === 'court') {
      if (!newItem || courtsSelected.includes(newItem)) return;

      setCourtsSelected([...courtsSelected, newItem]);
      const newFilter = { ...filter, courts: [...courtsSelected, newItem] };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('court', '');
    }

    if (type === 'client') {
      if (!newItem || clientsSelected.some((c) => c.id === newItem)) return;
      const client = clients?.find((c) => c.id === newItem);
      if (!client) return;

      setClientsSelected([...clientsSelected, client]);
      const newFilter = { ...filter, clientIds: [...clientsSelected, client].map((c) => c.id) };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'status') {
      if (newItem === undefined || statusSelected.some((s) => s === newItem)) return;

      setStatusSelected([...statusSelected, newItem]);
      const newFilter = { ...filter, statusList: [...statusSelected, newItem] };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'paymentStatus') {
      if (newItem === undefined || paymentStatusSelected.some((s) => s === newItem)) return;

      setPaymentStatusSelected([...paymentStatusSelected, newItem]);
      const newFilter: IFilter = {
        ...filter,
        paymentStatusList: [...paymentStatusSelected, newItem],
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'specialty') {
      if (!newItem || specialitiesSelected.some((s) => s.id === newItem)) return;
      const specialty = specialties?.find((s) => s.id === newItem);
      if (!specialty) return;

      setSpecialitiesSelected([...specialitiesSelected, specialty]);
      const newFilter = {
        ...filter,
        specialtiesIds: [...specialitiesSelected, specialty].map((s) => s.id),
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'typeDemand') {
      if (newItem === undefined || demandTypeSelected.some((d) => d === newItem)) return;

      setDemandTypeSelected([...demandTypeSelected, newItem]);
      const newFilter = { ...filter, correspondenceTypes: [...demandTypeSelected, newItem] };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      const newItemType = Number(newItem) as CorrespondenceType;
      setBaseCorrespondences(correspondences?.filter((c) => c.type === newItemType) || []);
    }

    if (type === 'correspondence') {
      if (!newItem || correspondencesSelected.some((c) => c.id === newItem)) return;
      const correspondence = correspondences?.find((c) => c.id === newItem);
      if (!correspondence) return;

      setCorrespondenceSelected([...correspondencesSelected, correspondence]);
      const newFilter = {
        ...filter,
        correspondencesIds: [...correspondencesSelected, correspondence].map((c) => c.id),
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'correspondent') {
      if (!newItem || correspondentsSelected.some((c) => c.id === newItem)) return;
      const correspondent = paymentCorrespondents?.find((c) => c.id === newItem);
      if (!correspondent) return;

      setCorrespondentsSelected([
        ...correspondentsSelected,
        { ...(correspondent as unknown as ICorrespondentResultViewModel) },
      ]);
      const newFilter = {
        ...filter,
        correspondentsIds: [...correspondentsSelected, correspondent].map((c) => c.id),
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'audienceType') {
      if (newItem === undefined || typeAudienceSelected.some((t) => t === newItem)) return;

      setTypeAudienceSelected([...typeAudienceSelected, newItem]);
      const newFilter = { ...filter, typeAudienceList: [...typeAudienceSelected, newItem] };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }
  };

  const handleRemoveItemFilterSelected = (idItem: string, type: TypeFilterItem) => {
    if (type === 'dostrictUfAndName') {
      const newDistrictsSelected = districtsSelected.filter((c) => c.id !== idItem);
      setDistrictsSelected(newDistrictsSelected);

      const newFilter = {
        ...filter,
        districts: newDistrictsSelected,
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'protocol') {
      const newProtocolsSelected = protocolsSelected.filter((c) => c !== idItem);
      setProtocolsSelected(newProtocolsSelected);
      const newFilter = { ...filter, protocols: newProtocolsSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('protocol', '');
    }

    if (type === 'processNumber') {
      const newProcessNumbersSelected = processNumbersSelected.filter((c) => c !== idItem);
      setProcessNumbersSelected(newProcessNumbersSelected);
      const newFilter = { ...filter, processNumbers: newProcessNumbersSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('processNumber', '');
    }

    if (type === 'folderNumber') {
      const newFoldersNumerSelected = foldersNumersSelected.filter((c) => c !== idItem);
      setFoldersNumersSelected(newFoldersNumerSelected);
      const newFilter = { ...filter, foldersNumbers: newFoldersNumerSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('folderNumber', '');
    }

    if (type === 'partName') {
      const newPartsNameSelected = partsNamesSelected.filter((c) => c !== idItem);
      setPartsNamesSelected(newPartsNameSelected);
      const newFilter = { ...filter, partsNames: newPartsNameSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('partName', '');
    }

    if (type === 'client') {
      const newClientsSelected = clientsSelected?.filter((c) => c.id !== idItem);
      setClientsSelected(newClientsSelected);
      const newFilter = { ...filter, clientIds: newClientsSelected.map((c) => c.id) };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'status') {
      const newStatusSelected = statusSelected?.filter((s) => s !== idItem);
      setStatusSelected(newStatusSelected);
      const newFilter = { ...filter, statusList: newStatusSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'paymentStatus') {
      const newStatusSelected = paymentStatusSelected?.filter((s) => s !== idItem);
      setPaymentStatusSelected(newStatusSelected);
      const newFilter = { ...filter, paymentStatusList: newStatusSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'specialty') {
      const newSpecialitiesSelected = specialitiesSelected.filter((s) => s.id !== idItem);
      setSpecialitiesSelected(newSpecialitiesSelected);
      const newFilter = { ...filter, specialtiesIds: newSpecialitiesSelected.map((s) => s.id) };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'typeDemand') {
      const newDemandTypeSelected = demandTypeSelected?.filter((s) => s !== idItem);
      const newFilter = { ...filter, correspondenceTypes: newDemandTypeSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      if (newDemandTypeSelected.length) {
        setBaseCorrespondences(
          correspondences.filter((c) => newDemandTypeSelected.includes(c.type.toString())),
        );
      } else {
        setBaseCorrespondences(correspondences);
      }

      setDemandTypeSelected(newDemandTypeSelected);
    }

    if (type === 'correspondence') {
      const newCorrespondencesSelected = correspondencesSelected.filter((c) => c.id !== idItem);
      setCorrespondenceSelected(newCorrespondencesSelected);
      const newFilter = {
        ...filter,
        correspondencesIds: newCorrespondencesSelected.map((c) => c.id),
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'court') {
      const newCourtsSelected = courtsSelected?.filter((c) => c !== idItem);
      setCourtsSelected(newCourtsSelected);
      const newFilter = { ...filter, courts: newCourtsSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
      setValue('court', '');
    }

    if (type === 'correspondent') {
      const newCorrespondentsSelected = correspondentsSelected.filter((c) => c.id !== idItem);
      setCorrespondentsSelected(newCorrespondentsSelected);
      const newFilter = {
        ...filter,
        correspondentsIds: newCorrespondentsSelected.map((c) => c.id),
      };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }

    if (type === 'audienceType') {
      const newTypeAudienceSelected = typeAudienceSelected.filter((c) => c !== idItem);
      setTypeAudienceSelected(newTypeAudienceSelected);
      const newFilter = { ...filter, typeAudienceList: newTypeAudienceSelected };
      setFilter(newFilter);
      onChangeFilter(newFilter);
    }
  };

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valueDate = event.target.value;
    setSolicitationDateType(Number(valueDate));
    if (hasErrorDate.hasError) return;

    if (startDate && endDate) {
      onChangeFilter({
        start: startDate,
        end: endDate,
        filterDateType: Number(valueDate),
      });
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    if (!date || !endDate) {
      setHasErrorDate({ hasError: true, message: 'Selecione as duas datas' });
    }
    if (date && endDate) {
      if (date > endDate) {
        setHasErrorDate({
          hasError: true,
          message: 'Data inicial não pode ser maior que a data final',
        });
        return;
      }
      setHasErrorDate({ hasError: false, message: '' });
      onChangeFilter({
        start: date,
        end: endDate,
        filterDateType: solicitationDateType,
      });
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date === null) return;
    if (!isValidDate(date)) return;
    setEndDate(date);
    if (!startDate || !date) {
      setHasErrorDate({ hasError: true, message: 'Selecione as duas datas' });
      return;
    }
    if (startDate && date) {
      if (startDate > date) {
        setHasErrorDate({
          hasError: true,
          message: 'Data inicial não pode ser maior que a data final',
        });
        return;
      }
      setHasErrorDate({ hasError: false, message: '' });
      onChangeFilter({
        end: date,
        start: startDate,
        filterDateType: solicitationDateType,
      });
    }
  };

  const fetchDistrictsByUfCode = async (ufCode: number) => {
    setLoading(true);
    await DistrictController.getDistrictsByUfCode(ufCode)
      .then((res) => {
        setDistricts(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (correspondences) {
      setBaseCorrespondences(correspondences);
    }
  }, [correspondences]);

  useEffect(() => {
    if (districtUfCode !== 0) {
      fetchDistrictsByUfCode(districtUfCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtUfCode]);

  useEffect(() => {
    setEnableClientsLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container id="left-filter">
      <PanelFilter isCompany={[TypeUser.COMPANY, TypeUser.ADM].includes(user.type)}>
        <div className="headerFilter">
          <div className="header-title">
            <Filter />
            <h1>Filtro</h1>
          </div>
          <button type="button" className="btnBorderless" onClick={handleCleanFilter}>
            <IconClean /> Limpar
          </button>
        </div>
        <div className="contentFilter">
          {displayOptions.protocol && (
            <div className="containerSelect">
              <h2>
                Protocolo
                <Tooltip
                  disableInteractive
                  title="Dica: Pressione Enter para adicionar um novo item à lista.">
                  <Info style={{ fontSize: '1rem' }} />
                </Tooltip>
              </h2>

              <div className="input">
                <FormControl fullWidth>
                  <Input
                    name="protocol"
                    control={control}
                    placeholder="Digite o protocolo"
                    variant="outlined"
                    color="primary"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddItemFilterSelected(protocolNumber, 'protocol');
                      }
                    }}
                  />
                </FormControl>
              </div>
              <div className="containerSummary">
                {protocolsSelected.map((p) => (
                  <div className="contentMap" key={p}>
                    <p>{p}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(p, 'protocol')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.processNumber && (
            <div className="containerSelect">
              <h2>
                Número de Processo{' '}
                <Tooltip
                  disableInteractive
                  title="Dica: Pressione Enter para adicionar um novo item à lista.">
                  <Info style={{ fontSize: '1rem' }} />
                </Tooltip>
              </h2>
              <div className="input">
                <FormControl fullWidth>
                  <Input
                    name="processNumber"
                    control={control}
                    placeholder="Digite o número de processo"
                    variant="outlined"
                    color="primary"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddItemFilterSelected(processNumber, 'processNumber');
                      }
                    }}
                    mask={MASK_PROCESS_NUMBER}
                  />
                </FormControl>
              </div>
              <div className="containerSummary">
                {processNumbersSelected.map((p) => (
                  <div className="contentMap" key={p}>
                    <p>{p}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(p, 'processNumber')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.folderName && (
            <div className="containerSelect">
              <h2>
                Número da Pasta Interno{' '}
                <Tooltip
                  disableInteractive
                  title="Dica: Pressione Enter para adicionar um novo item à lista.">
                  <Info style={{ fontSize: '1rem' }} />
                </Tooltip>
              </h2>
              <div className="input">
                <FormControl fullWidth>
                  <Input
                    name="folderNumber"
                    control={control}
                    placeholder="Digite o nº da pasta"
                    variant="outlined"
                    color="primary"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddItemFilterSelected(folderNumber, 'folderNumber');
                      }
                    }}
                  />
                </FormControl>
              </div>
              <div className="containerSummary">
                {foldersNumersSelected.map((folder) => (
                  <div className="contentMap" key={folder}>
                    <p>{folder}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(folder, 'folderNumber')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.partName && (
            <div className="containerSelect">
              <h2>
                Nome da Parte{' '}
                <Tooltip
                  disableInteractive
                  title="Dica: Pressione Enter para adicionar um novo item à lista.">
                  <Info style={{ fontSize: '1rem' }} />
                </Tooltip>
              </h2>
              <div className="input">
                <FormControl fullWidth>
                  <Input
                    name="partName"
                    control={control}
                    placeholder="Digite o nome da parte"
                    variant="outlined"
                    color="primary"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddItemFilterSelected(partName, 'partName');
                      }
                    }}
                  />
                </FormControl>
              </div>
              <div className="containerSummary">
                {partsNamesSelected.map((part) => (
                  <div className="contentMap" key={part}>
                    <p>{part}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(part, 'partName')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.court && (
            <div className="containerSelect">
              <h2>Vara ou Juizado</h2>
              <div className="input">
                <FormControl fullWidth>
                  <Input
                    name="court"
                    control={control}
                    placeholder="Digite a vara ou juizado"
                    variant="outlined"
                    color="primary"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddItemFilterSelected(court, 'court');
                      }
                    }}
                  />
                </FormControl>
              </div>
              <div className="containerSummary">
                {courtsSelected?.map((c) => (
                  <div className="contentMap" key={c}>
                    <p>{c}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(c, 'court')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.client && (
            <div className="containerSelect">
              <h2>Cliente</h2>
              <div className="dropDown">
                <DropDownList
                  isSearchable
                  itens={clients?.map((c) => ({ id: c.id, value: c.name })) || []}
                  placeholderTitle="Selecione"
                  clearToSelect
                  typeReturn="id"
                  onChange={(e) => {
                    const clientSelectedId = e as string;
                    handleAddItemFilterSelected(clientSelectedId, 'client');
                  }}
                />
              </div>
              <div className="containerSummary">
                {clientsSelected?.map((c) => (
                  <div className="contentMap" key={c.id}>
                    <p>{c.name}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(c.id, 'client')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.status && (
            <div className="containerSelect">
              <h2>Status</h2>
              <div className="dropDown">
                <DropDownList
                  placeholderTitle="Selecione"
                  clearToSelect
                  typeReturn="id"
                  onChange={(e) => {
                    const status = e as string;
                    handleAddItemFilterSelected(status, 'status');
                  }}
                  itens={Object.keys(ServiceStatus)
                    .filter((statusType) => !isNaN(Number(statusType)))
                    .filter((status) => {
                      if (toFinancial) {
                        return (
                          status === ServiceStatus.PENDING.toString() ||
                          status === ServiceStatus.UNPAID.toString()
                        );
                      }
                      return true;
                    })
                    .map((status) => ({
                      id: status,
                      value: labelServiceStatus[status as unknown as ServiceStatus],
                    }))}
                />
              </div>
              <div className="containerSummary">
                {statusSelected.map((statusIndex) => (
                  <div className="contentMap" key={statusIndex}>
                    <p>{labelServiceStatus[statusIndex as unknown as ServiceStatus]}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(statusIndex, 'status')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.paymentStatus && (
            <div className="containerSelect">
              <h2>Status do Pagamento</h2>
              <div className="dropDown">
                <DropDownList
                  placeholderTitle="Selecione"
                  clearToSelect
                  typeReturn="id"
                  onChange={(e) => {
                    const status = e as string;
                    handleAddItemFilterSelected(status, 'paymentStatus');
                  }}
                  itens={Object.keys(PaymentSolicitationStatus)
                    .filter((statusType) => !isNaN(Number(statusType)))
                    .map((status) => ({
                      id: status,
                      value: labelPaymentStatus[status as unknown as PaymentSolicitationStatus],
                    }))}
                />
              </div>
              <div className="containerSummary">
                {paymentStatusSelected.map((statusIndex) => (
                  <div className="contentMap" key={statusIndex}>
                    <p>{labelPaymentStatus[statusIndex as unknown as PaymentSolicitationStatus]}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(statusIndex, 'paymentStatus')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.district && (
            <div>
              <h2>Comarca</h2>
              <div className="containerDistricts">
                <div className="uf">
                  <Controller
                    control={control}
                    name="districtUfCode"
                    render={({ field: { onChange } }) => (
                      <DropDownList
                        placeholderTitle="Estado"
                        itens={BRAZILIAN_STATES.map((s) => ({ id: s.ufCode, value: s.name })) ?? []}
                        width="100%"
                        typeReturn="id"
                        value={districtUfCode || ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>

                <div className="district">
                  <FormControl fullWidth>
                    <DropDownList
                      itens={districts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((district) => ({
                          id: district.id,
                          value: district.name,
                        }))}
                      placeholderTitle="Selecione"
                      typeReturn="id"
                      onChange={(e) => {
                        const districtId =
                          districts.find((d) => d.id === e && d.ufCode === districtUfCode)?.id ||
                          '';
                        handleAddItemFilterSelected(districtId, 'dostrictUfAndName');
                      }}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="containerSummary">
                {districtsSelected.map((district) => (
                  <div className="contentMap" key={`${district.name}${district.ufCode}`}>
                    <p>{`${district.name} - ${
                      Object.entries(UF_CODE_BY_NAME).find(
                        ([, value]) => value === Number(district.ufCode),
                      )?.[0]
                    }`}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() =>
                        handleRemoveItemFilterSelected(district.id!, 'dostrictUfAndName')
                      }>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.correspondent && (
            <div className="containerSelect">
              <h2>Correspondente</h2>
              <div className="dropDown">
                <FormControl fullWidth>
                  <DropDownList
                    isSearchable
                    itens={paymentCorrespondents.map((correspondent) => ({
                      id: correspondent.id,
                      value: `${correspondent.firstName} ${correspondent.lastName}`,
                    }))}
                    placeholderTitle="Selecione"
                    clearToSelect
                    typeReturn="id"
                    onChange={(e) => {
                      const correspondentId = e as string;
                      handleAddItemFilterSelected(correspondentId, 'correspondent');
                    }}
                    fetchNextPage={fetchNextPageCorrespondents}
                    hasNextPage={hasNextPageCorrespondents}
                    isFetchingNextPage={isFetchingNextPageCorrespondents}
                  />
                </FormControl>
              </div>
              <div className="containerSummary">
                {correspondentsSelected.map((correspondent) => (
                  <div className="contentMap" key={correspondent.id}>
                    <p>{`${correspondent.firstName} ${correspondent.lastName}`}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() =>
                        handleRemoveItemFilterSelected(correspondent.id, 'correspondent')
                      }>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.date && (
            <div className="containerDistanceFilter">
              <h2>Data</h2>
              <div className="checkedControl">
                <div>
                  <Checkbox
                    checked={solicitationDateType === GetSolicitationFilterDateType.FROM_START}
                    name="radio-buttons"
                    value={GetSolicitationFilterDateType.FROM_START}
                    onChange={handleCheckbox}
                  />
                  <p>Data de criação</p>
                </div>
                <div>
                  <Checkbox
                    checked={solicitationDateType === GetSolicitationFilterDateType.FROM_EXPIRATION}
                    name="radio-buttons"
                    value={GetSolicitationFilterDateType.FROM_EXPIRATION}
                    onChange={handleCheckbox}
                  />
                  <p>Data de vencimento</p>
                </div>
              </div>
              <div className="contentDistanceFilter">
                <label htmlFor="date-field">Desde</label>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    aria-labelledby="date-field"
                    value={startDate}
                    onChange={handleStartDateChange}
                    onError={(e: any) => {
                      if (!e) {
                        if (startDate && endDate) {
                          setHasErrorDate({ hasError: false, message: '' });
                          return;
                        }
                        setHasErrorDate({ hasError: true, message: 'Selecione as duas datas' });
                      }
                      if (e === 'invalidDate')
                        setHasErrorDate({ hasError: true, message: 'Data inicial inválida' });
                      if (e === 'minDate')
                        setHasErrorDate({
                          hasError: true,
                          message: 'Data inicial deve ser maior que 01/01/1970',
                        });
                      if (e === 'maxDate')
                        setHasErrorDate({
                          hasError: true,
                          message: 'Data inicial deve ser menor ou igual a data final',
                        });
                    }}
                  />
                </LocalizationProvider>
              </div>
              <div className="contentDistanceFilter">
                <label htmlFor="date-field-end">Até</label>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    aria-labelledby="date-field-end"
                    value={endDate}
                    onChange={handleEndDateChange}
                    onError={(e: any) => {
                      if (!e) {
                        if (startDate && endDate) {
                          setHasErrorDate({ hasError: false, message: '' });
                          return;
                        }
                        setHasErrorDate({ hasError: true, message: 'Selecione as duas datas' });
                      }
                      if (e === 'invalidDate')
                        setHasErrorDate({ hasError: true, message: 'Data final inválida' });
                      if (e === 'minDate')
                        setHasErrorDate({
                          hasError: true,
                          message: 'Data final deve ser maior ou igual que a data inicial',
                        });
                    }}
                    minDate={new Date(1970, 0, 1)}
                  />
                </LocalizationProvider>
              </div>
              {hasErrorDate.hasError && (
                <span className="fieldError">
                  <ErrorIcon fontSize="inherit" /> {hasErrorDate.message}
                </span>
              )}
            </div>
          )}
          {displayOptions.especilty && (
            <div className="containerSelect">
              <h2>Especialidade</h2>
              <div className="dropDown">
                <DropDownList
                  placeholderTitle="Selecione"
                  isSearchable
                  itens={specialties?.map((s) => ({ id: s.id, value: s.name })) || []}
                  clearToSelect
                  typeReturn="id"
                  onChange={(e) => {
                    const specialtyId = e as string;
                    handleAddItemFilterSelected(specialtyId, 'specialty');
                  }}
                />
              </div>
              <div className="containerSummary">
                {specialitiesSelected.map((s) => (
                  <div className="contentMap" key={s.id}>
                    <p>{s.name}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(s.id, 'specialty')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.correspondenceType && (
            <div className="containerSelect">
              <h2>Tipo de Demanda</h2>
              <div className="dropDown">
                <DropDownList
                  placeholderTitle="Selecione"
                  itens={Object.keys(CorrespondenceType)
                    .filter((correspondenceType) => !isNaN(Number(correspondenceType)))
                    .map((correspondenceType) => ({
                      id: correspondenceType,
                      value:
                        labelCorrespondenceType[
                          correspondenceType as unknown as CorrespondenceType
                        ],
                    }))}
                  typeReturn="id"
                  clearToSelect
                  onChange={(e) => {
                    const typeDemand = e as string;
                    handleAddItemFilterSelected(typeDemand, 'typeDemand');
                  }}
                />
              </div>
              <div className="containerSummary">
                {demandTypeSelected.map((type) => (
                  <div className="contentMap" key={type}>
                    <p>{labelCorrespondenceType[type as unknown as CorrespondenceType]}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(type, 'typeDemand')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.correspondence && (
            <div className="containerSelect">
              <h2>Correspondências</h2>
              <div className="dropDown">
                <DropDownList
                  isSearchable
                  placeholderTitle="Selecione"
                  itens={baseCorrespondences?.map((c) => ({ id: c.id, value: c.name })) || []}
                  clearToSelect
                  typeReturn="id"
                  onChange={(e) => {
                    const correspondenceId = e as string;
                    handleAddItemFilterSelected(correspondenceId, 'correspondence');
                  }}
                />
              </div>
              <div className="containerSummary">
                {correspondencesSelected.map((c) => (
                  <div className="contentMap" key={c.id}>
                    <p>{c.name}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(c.id, 'correspondence')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayOptions.audienceType && (
            <div className="containerSelect">
              <h2>Tipo Audiência</h2>
              <div className="dropDown">
                <DropDownList
                  placeholderTitle="Selecione"
                  itens={Object.keys(AudienceType)
                    .filter((audienceType) => !isNaN(Number(audienceType)))
                    .map((audienceType) => ({
                      id: audienceType,
                      value: labelAudienceType[audienceType as unknown as AudienceType],
                    }))}
                  clearToSelect
                  typeReturn="id"
                  onChange={(e) => {
                    const typeAudience = e as string;
                    handleAddItemFilterSelected(typeAudience, 'audienceType');
                  }}
                />
              </div>
              <div className="containerSummary">
                {typeAudienceSelected.map((typeIndex) => (
                  <div className="contentMap" key={typeIndex}>
                    <p>{labelAudienceType[typeIndex as unknown as AudienceType]}</p>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => handleRemoveItemFilterSelected(typeIndex, 'audienceType')}>
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </PanelFilter>
    </Container>
  );
}
